import { Mark, mergeAttributes } from '@tiptap/react';
import type { NoteDocument } from '@/types/sessions';

/**
 * Inline marks for session notes. Each mark instance gets its own `uid`; the
 * backend (SessionNotes::MarkExtractor) mirrors highlight / followUp marks
 * into session_note_marks by that uid, so a follow-up ticked off later keeps
 * its state when the note is edited again.
 */
export function newMarkUid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

const uidAttribute = {
  uid: {
    default: null,
    parseHTML: (element: HTMLElement) => element.getAttribute('data-uid'),
    renderHTML: (attributes: { uid?: string | null }) => (attributes.uid ? { 'data-uid': attributes.uid } : {}),
  },
};

/** "Wichtig": a highlighted passage, collected on the client's record. */
export const ImportantMark = Mark.create({
  name: 'highlight',
  addAttributes: () => uidAttribute,
  parseHTML: () => [{ tag: 'mark[data-kind="highlight"]' }],
  renderHTML: ({ HTMLAttributes }) => ['mark', mergeAttributes({ 'data-kind': 'highlight', class: 'sn-highlight' }, HTMLAttributes), 0],
});

/** "Follow-up": shows up in the next session's note ("Aus der letzten Sitzung"). */
export const FollowUpMark = Mark.create({
  name: 'followUp',
  addAttributes: () => uidAttribute,
  parseHTML: () => [{ tag: 'span[data-kind="follow-up"]' }],
  renderHTML: ({ HTMLAttributes }) => ['span', mergeAttributes({ 'data-kind': 'follow-up', class: 'sn-follow-up' }, HTMLAttributes), 0],
});

/** A new note starts empty; the editor shows its placeholder. */
export const EMPTY_DOC: NoteDocument = { type: 'doc', content: [{ type: 'paragraph' }] };
